import {formatUsdAsCredits,formatCredits,providerUsdFromCredits} from '../src/credit-units';
import {ErrorNotice, Loading} from './components';
import type {HermesState, NativeTokenSnapshot} from './lib/types';
import type {TeamMemberView} from './team-workspace';
import {StorageUsage} from './storage-usage';
import './workspace-usage.css';
import {APP_BASE,Link} from './router';

const tokenNumber = new Intl.NumberFormat(undefined, {maximumFractionDigits: 0});
const dollars = new Intl.NumberFormat('en-US', {style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2});

function currentTokens(state: HermesState): NativeTokenSnapshot | null {
  const snapshot = state.nativeTokens.data;
  return state.nativeTokens.status === 'ready' && state.nativeConnection.status === 'open'
    && snapshot?.sessionId === state.nativeTarget?.sessionId ? snapshot : null;
}

export function ConversationCost({state, sessionId}: {state: HermesState; sessionId: string | null}) {
  const credit = state.accountBalance;
  if (credit.status === 'error' || credit.data?.status === 'ready') return <section className="workspace-balance" aria-label="Account credits">
    <ErrorNotice error={credit.error}/>
    {credit.data?.status === 'ready' && <strong>{formatCredits(credit.data.creditBalance.spendable)} credits</strong>}
    <Loading active={credit.status === 'loading'}/>
  </section>;
  const current = currentTokens(state);
  const estimate = current?.sessionId === sessionId ? current.costEstimate : null;
  return <section className="workspace-balance" aria-label="Estimated cost for this chat"><span>Estimated cost</span>
    <ErrorNotice error={state.nativeTokens.error}/>
    {estimate?.status === 'error' && <ErrorNotice error={estimate.error}/>}
    {estimate?.status === 'ready' ? <strong title={`Recorded usage in this chat and its bots, at published standard token rates checked ${estimate.rateDate}. Includes normal input, cache reads, cache writes, and output. Other model tools use Hermes's recorded cost. Context and processing surcharges and memory-service calls are not included. Updates after reported usage changes.`}>{dollars.format(estimate.costUsd)}</strong>
      : <Loading active={state.nativeTokens.status === 'loading' && state.nativeConnection.status === 'open'}/>}
  </section>;
}

export function BotSessionTokens({state, member}: {state: HermesState; member: TeamMemberView}) {
  const snapshot = currentTokens(state);
  const usage = snapshot?.agents.find(agent => agent.sessionId === member.sessionId);
  if (!usage) return null;
  const description = `${tokenNumber.format(usage.inputTokens)} input tokens and ${tokenNumber.format(usage.outputTokens)} output tokens reported in this chat. The current model response is not counted until OpenAI reports its usage.`;
  return <span className="bot-session-tokens" title={usage.costEstimate.status === 'error'
    ? `${description}\n${usage.costEstimate.error.message}\n${usage.costEstimate.error.stack}` : description}>
      {tokenNumber.format(usage.totalTokens)} tokens
      {usage.costEstimate.status === 'ready' && <> / {dollars.format(usage.costEstimate.costUsd)}</>}
  </span>;
}

export function CreditAllowance({state}:{state:HermesState}) {
  const resource=state.accountBalance,credit=resource.data;
  const allowance=credit?.status==='ready'?credit.allowance:null;
  const weekly=allowance?.weekly;
  const permanent=credit?.status==='ready'?providerUsdFromCredits(credit.creditBalance.permanent):0;
  const limit=weekly?weekly.limitUsd+weekly.resetUsd+permanent:(credit?.status==='ready'?credit.grantedUsd:0);
  const remaining=credit?.status==='ready'?providerUsdFromCredits(credit.creditBalance.spendable):0;
  const progress=remaining;
  const percentage=limit>0?Math.max(0,Math.min(100,progress/limit*100)):0;
  return <section className="credit-allowance" aria-label="Credit allowance"><p className="eyebrow">{allowance?`${allowance.planName} · available credits`:'Account credits'}</p>
    <ErrorNotice error={resource.error}/><Loading active={resource.status==='loading'&&!credit}/>
    {credit && <><div className="credit-allowance-label"><strong>{formatUsdAsCredits(progress)} / {formatUsdAsCredits(limit)} remaining</strong></div>
      <div className="credit-meter" role="progressbar" aria-label={weekly?"Spendable subscription and admin credits":"Credits remaining"} aria-valuemin={0} aria-valuemax={100} aria-valuenow={percentage}
        aria-valuetext={`${formatUsdAsCredits(progress)} credits remaining out of ${formatUsdAsCredits(limit)} credits`}><span style={{width:`${percentage}%`}}/></div>
      <p className="credit-allowance-note">{weekly?`Renews ${new Date(weekly.endsAt).toLocaleDateString()}`:credit.status==='not-funded'?'Choose a plan to fund your team':'Saved credit balance'} · <Link href={`${APP_BASE}/billing`}>Billing</Link></p>
      {weekly&&permanent>0&&<p className="credit-allowance-note">Includes permanent admin credits.</p>}
      {credit.status==='ready' && (credit.reservedUsd??0)>0 && <p className="credit-allowance-note">{formatUsdAsCredits(credit.reservedUsd!)} credits reserved for pending work</p>}
    </>}
  </section>;
}

export function WorkspaceUsage({state}: {state: HermesState}) {
  return <div className="workspace-usage" aria-label="Credits and storage">
    <CreditAllowance state={state}/><StorageUsage state={state} compact/>
  </div>;
}
